import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { AppState } from '../../reducers/root.reducer';
import { RequestServiceResponse } from '@tcc-online/request-service';
import UploadState from '../../types/UploadState';
import Participant360 from '../types/project/360/Participant360';
import Admin360ParticipantService, {
    ParticipantPayload,
    CreateEvaluatorPayload,
    EditEvaluatorPayload,
    EvaluatorPayload,
} from '../services/Admin360ParticipantService';
import Evaluator from '../types/project/360/Evaluator';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import ImportService from '../services/ImportService';
import ImportProcess from '../../common/types/ImportProcess';
import ImportStates, { ImportState } from '../constants/ImportStates';
import type { ParticipantImportPreviewResponse } from '../types/ParticipantImportPreviewResponse';

const NAME = 'Admin360Participant';

const getParticipants = createNotifiedThunk(
    `${NAME}/getParticipants`,
    Admin360ParticipantService.getParticipants,
    { error: 'fe.admin.error.load-participants' },
);
const getParticipant = createNotifiedThunk(
    `${NAME}/getParticipant`,
    Admin360ParticipantService.getParticipant,
    { error: 'fe.admin.error.load-participant' },
);
const createParticipant = createNotifiedThunk(
    `${NAME}/createParticipant`,
    Admin360ParticipantService.createParticipant,
    {
        error: 'fe.admin.error.create-participant',
        success: 'fe.admin.success.create-participant',
    },
);
const deleteParticipant = createNotifiedThunk(
    `${NAME}/deleteParticipant`,
    Admin360ParticipantService.deleteParticipant,
    { error: 'fe.admin.error.delete-participant' },
);
const importParticipants = createNotifiedThunk(
    `${NAME}/importParticipants`,
    Admin360ParticipantService.importParticipants,
    { error: 'fe.admin.error.import-participants' },
);
const confirmImport = createNotifiedThunk(
    `${NAME}/confirmImport`,
    ImportService.confirmImport,
    { error: 'fe.admin.error.import-file' },
);
const cancelImport = createNotifiedThunk(
    `${NAME}/cancelImport`,
    ImportService.cancelImport,
    { error: 'fe.admin.error.cancel-import' },
);
const getImportProgress = createNotifiedThunk(
    `${NAME}/getImportProgress`,
    ImportService.getImportProgress,
    { error: 'fe.admin.error.import-users' },
);
const exportParticipants = createNotifiedThunk(
    `${NAME}/exportParticipants`,
    Admin360ParticipantService.exportParticipants,
    { error: 'fe.admin.error.export-participants' },
);
const getImportTemplate = createNotifiedThunk(
    `${NAME}/getImportTemplate`,
    Admin360ParticipantService.getImportTemplate,
    { error: 'fe.admin.error.fetch-template' },
);

const getEvaluators = createNotifiedThunk(
    `${NAME}/getEvaluators`,
    Admin360ParticipantService.getEvaluators,
    { error: 'fe.admin.error.load-evaluators' },
);
const getEvaluator = createNotifiedThunk(
    `${NAME}/getEvaluator`,
    Admin360ParticipantService.getEvaluator,
    { error: 'fe.admin.error.load-evaluators' },
);
const createEvaluator = createNotifiedThunk(
    `${NAME}/createEvaluator`,
    Admin360ParticipantService.createEvaluator,
    { error: 'fe.admin.error.create-evaluator' },
);
const editEvaluator = createNotifiedThunk(
    `${NAME}/editEvaluator`,
    Admin360ParticipantService.editEvaluator,
    { error: 'fe.admin.error.update-evaluator' },
);
const deleteEvaluator = createNotifiedThunk(
    `${NAME}/deleteEvaluator`,
    Admin360ParticipantService.deleteEvaluator,
    { error: 'fe.admin.error.delete-evaluator' },
);
const batchDeleteEvaluators = createNotifiedThunk(
    `${NAME}/batchDeleteEvaluators`,
    Admin360ParticipantService.batchDeleteEvaluators,
    {
        error: 'fe.admin.error.batch-delete-evaluators',
        success: 'fe.admin.success.batch-delete-evaluators',
    },
);
const deleteAllEvaluators = createNotifiedThunk(
    `${NAME}/deleteAllEvaluators`,
    Admin360ParticipantService.deleteAllEvaluators,
    {
        error: 'fe.admin.error.delete-all-evaluators',
        success: 'fe.admin.success.delete-all-evaluators',
    },
);

export interface Admin360ParticipantState {
    list: UploadState<Participant360[]>;
    import: {
        preview: ParticipantImportPreviewResponse | null;
        process: ImportProcess | null;
        state: ImportState;
        importId: number | null;
    };
}

const initialState: Admin360ParticipantState = {
    list: {
        data: [],
        loading: false,
        uploading: false,
    },
    import: {
        preview: null,
        process: null,
        state: ImportStates.Idle,
        importId: ImportService.getImportId(),
    },
};

function resetImport(state: Admin360ParticipantState): void {
    state.import.state = ImportStates.Idle;
    state.import.importId = null;
    state.import.preview = null;
    state.import.process = null;
}

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        resetImport: (state: Admin360ParticipantState): void =>
            resetImport(state),
    },
    extraReducers: (builder): void => {
        builder.addCase(
            getParticipants.pending,
            (state: Admin360ParticipantState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            getParticipants.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<RequestServiceResponse<Participant360[]>>,
            ): void => {
                state.list.data = action.payload.data;
            },
        );

        builder.addCase(
            createParticipant.pending,
            (state: Admin360ParticipantState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            createParticipant.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<RequestServiceResponse<Participant360>>,
            ): void => {
                if (state.list.data?.length >= 0) {
                    state.list.data.push({
                        ...action.payload.data,
                        evaluators: action.payload.data.evaluators ?? [],
                    });
                }
            },
        );

        builder.addCase(
            editEvaluator.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<
                    RequestServiceResponse<Evaluator>,
                    string,
                    { arg: EditEvaluatorPayload & EvaluatorPayload }
                >,
            ): void => {
                const participants: Participant360[] = state.list.data;

                const participantIndex: number | null =
                    findParticipantIndexById(
                        participants,
                        action.meta.arg.participantId,
                    );
                if (participantIndex === null) {
                    return;
                }

                const evaluatorIndex: number = findEvaluatorIndex(
                    participants[participantIndex].evaluators,
                    action.payload.data?.id,
                );

                if (evaluatorIndex >= 0) {
                    state.list.data[participantIndex].evaluators[
                        evaluatorIndex
                    ] = action.payload.data;
                }
            },
        );

        builder.addCase(
            createEvaluator.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<
                    RequestServiceResponse<Evaluator>,
                    string,
                    { arg: CreateEvaluatorPayload & ParticipantPayload }
                >,
            ): void => {
                const participants: Participant360[] = state.list.data;
                const participantIndex: number | null =
                    findParticipantIndexById(
                        participants,
                        action.meta.arg.participantId,
                    );
                if (participantIndex === null) {
                    return;
                }

                const evaluators: Evaluator[] =
                    participants[participantIndex].evaluators;
                if (!evaluators) {
                    state.list.data[participantIndex].evaluators = [];
                }

                state.list.data[participantIndex].evaluators.push(
                    action.payload.data,
                );
            },
        );

        builder.addCase(
            deleteEvaluator.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<
                    RequestServiceResponse,
                    string,
                    { arg: EvaluatorPayload }
                >,
            ): void => {
                const participants: Participant360[] = state.list.data;
                const participantIndex: number | null =
                    findParticipantIndexById(
                        participants,
                        action.meta.arg.participantId,
                    );
                if (participantIndex === null) {
                    return;
                }

                const evaluatorIndex: number = findEvaluatorIndex(
                    participants[participantIndex].evaluators,
                    action.meta.arg.evaluatorId,
                );
                if (evaluatorIndex >= 0) {
                    state.list.data[participantIndex].evaluators.splice(
                        evaluatorIndex,
                        1,
                    );
                }
            },
        );

        builder.addCase(
            batchDeleteEvaluators.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                const { participantId, ids } = action.meta.arg;
                const participant = state.list.data.find(
                    (p) => p.id === participantId,
                );
                if (participant) {
                    participant.evaluators = participant.evaluators.filter(
                        (e) => !ids.includes(e.id),
                    );
                }
            },
        );

        builder.addCase(
            deleteAllEvaluators.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                const { participantId } = action.meta.arg;
                const participant = state.list.data.find(
                    (p) => p.id === participantId,
                );
                if (participant) {
                    participant.evaluators = [];
                }
            },
        );

        builder.addCase(
            deleteParticipant.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                if (!state.list.data) {
                    return;
                }

                const index: number = state.list.data.findIndex(
                    (participant: Participant360) =>
                        participant.id === action.meta.arg.participantId,
                );
                if (index >= 0) {
                    state.list.data.splice(index, 1);
                }
            },
        );

        builder.addCase(
            importParticipants.pending,
            (state: Admin360ParticipantState): void => {
                resetImport(state);
                state.import.state = ImportStates.Pending;
            },
        );
        builder.addCase(
            importParticipants.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                state.import.state = ImportStates.Preview;
                if (action.payload.data) {
                    state.import.importId = action.payload.data.importId;
                    state.import.preview = action.payload.data.preview;
                }
            },
        );
        builder.addCase(
            importParticipants.rejected,
            (state: Admin360ParticipantState): void => {
                resetImport(state);
                state.import.state = ImportStates.Fail;
            },
        );
        builder.addCase(
            confirmImport.pending,
            (state: Admin360ParticipantState): void => {
                state.import.state = ImportStates.Pending;
            },
        );
        builder.addCase(
            confirmImport.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                state.import.state = ImportStates.Importing;
                const data = action.payload.data as ImportProcess;
                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            confirmImport.rejected,
            (state: Admin360ParticipantState): void => {
                resetImport(state);
            },
        );
        builder.addCase(
            cancelImport.pending,
            (state: Admin360ParticipantState): void => {
                state.import.state = ImportStates.Idle;
                state.import.importId = null;
            },
        );
        builder.addCase(
            getImportProgress.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                const data = action.payload.data as ImportProcess;

                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            getImportProgress.rejected,
            (state: Admin360ParticipantState): void => {
                resetImport(state);
            },
        );

        builder.addMatcher(
            isAnyOf(getParticipants.pending, getParticipant.pending),
            (state: Admin360ParticipantState): void => {
                state.list.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getParticipants.fulfilled,
                getParticipants.rejected,
                getParticipant.fulfilled,
                getParticipant.rejected,
            ),
            (state: Admin360ParticipantState): void => {
                state.list.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(createParticipant.pending, deleteParticipant.pending),
            (state: Admin360ParticipantState): void => {
                state.list.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createParticipant.fulfilled,
                createParticipant.rejected,
                deleteParticipant.fulfilled,
                deleteParticipant.rejected,
            ),
            (state: Admin360ParticipantState): void => {
                state.list.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getImportProgress.rejected, importParticipants.rejected),
            (state: Admin360ParticipantState): void => {
                state.import.state = ImportStates.Idle;
            },
        );
    },
});

const state = (state: AppState): Admin360ParticipantState =>
    state.admin['360'].participant;
const participants = createSelector(
    state,
    (state: Admin360ParticipantState) => state.list,
);

interface Filters {
    text?: string;
    relationship?: string;
}

const filters = (_: AppState, filters: Filters) => filters;

const Admin360ParticipantSlice = {
    reducer: slice.reducer,
    actions: {
        getParticipants,
        getParticipant,
        createParticipant,
        deleteParticipant,
        getEvaluators,
        getEvaluator,
        createEvaluator,
        editEvaluator,
        deleteEvaluator,
        batchDeleteEvaluators,
        deleteAllEvaluators,
        importParticipants,
        confirmImport,
        cancelImport,
        getImportProgress,
        exportParticipants,
        getImportTemplate,
        ...slice.actions,
    },
    state: {
        all: state,
        participants,
        import: createSelector(
            state,
            (state: Admin360ParticipantState) => state.import,
        ),
        filteredParticipants: createSelector(
            [participants, filters],
            (participants: UploadState<Participant360[]>, filters: Filters) => {
                if (
                    (!filters.text || filters.text.trim() == '') &&
                    !filters.relationship
                ) {
                    return participants.data;
                }
                const filtered: Participant360[] = [];

                for (const participant of participants.data) {
                    const filteredEvaluators: Evaluator[] =
                        participant.evaluators.filter(filter(filters));

                    if (filteredEvaluators.length > 0) {
                        const newParticipant = {
                            ...participant,
                            evaluators: filteredEvaluators,
                        };
                        filtered.push(newParticipant);
                    }
                }

                return filtered;

                function filter(filters: Filters) {
                    return (evaluator: Evaluator) => {
                        const { text, relationship } = filters;
                        const name: string =
                            evaluator.email +
                            evaluator.lastName +
                            evaluator.firstName;
                        let regexp: RegExp | undefined;
                        if (text && text.trim() != '') {
                            regexp = new RegExp(
                                text.replace(/[{}()|[\]\\]/g, ''),
                                'i',
                            );
                        }

                        if (!regexp) {
                            return evaluator.relationship === relationship;
                        }

                        const test = regexp.test(name);
                        if (!relationship) {
                            return test;
                        }

                        return test && evaluator.relationship === relationship;
                    };
                }
            },
        ),
    },
};

export default Admin360ParticipantSlice;

function findParticipantIndexById(
    participants: Participant360[],
    participantId: number,
): number | null {
    if (!participants || participants.length === 0) {
        return null;
    }

    const participantIndex: number = participants.findIndex(
        (participant: Participant360) => participant.id === participantId,
    );
    return participantIndex >= 0 ? participantIndex : null;
}

function findEvaluatorIndex(
    evaluators: Evaluator[],
    evaluatorId: number,
): number {
    if (!evaluators || evaluators.length === 0) {
        return -1;
    }

    return evaluators.findIndex(
        (evaluator: Evaluator) => evaluator.id === evaluatorId,
    );
}
